import { Group, Mesh, PlaneGeometry, RepeatWrapping, ShaderMaterial, TextureLoader, use } from '@anov/3d-core'
import type { SceneControl } from '@anov/3d-core'
import RainShader from '../../shaders/rain'
import type { IRainOption } from '../../utils/constant'
import { Rain_Panel, Snow_panle } from '../../utils/constant'

const textureLoader = new TextureLoader()

const _defaultOption: IRainOption = {
  texture1: textureLoader.load(Snow_panle),
  texture2rain: textureLoader.load(Rain_Panel),
  size: 5.0,
  snowT1density: 0.1,
  rainT1density: 0.1,
  snowSpeed: 1.0,
  cameraPos: [0, 500.0, 0],
  rainSpeed: 3.0,
  rainAngle: 0.0,
}

class RainMesh extends Mesh {
  option: IRainOption = _defaultOption
  uniforms: any = {}
  loaded!: Promise<Boolean>
  sceneControl: SceneControl

  constructor(option: { width: number; height: number; segment: number }, sceneControl: SceneControl) {
    super()
    this.sceneControl = sceneControl
    const group = new Group()
    const { width, height } = option

    if (this.option.texture1)
      this.option.texture1.wrapS = this.option.texture1.wrapT = RepeatWrapping
    if (this.option.texture2rain)
      this.option.texture2rain.wrapS = this.option.texture2rain.wrapT = RepeatWrapping
    this.setUniforms({
      ...this.option,
      iTime: 0,
    })

    const createPlane = (x: number, y: number, z: number, rotationX: number, rotationY: number, rotationZ: number) => {
      const suiji = Math.random() * (width / 3)
      const geometry = new PlaneGeometry(width + suiji, height + suiji)
      const material = new ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: RainShader.vertexShaderFar,
        fragmentShader: RainShader.fragmentShaderFar,
        side: 2,
        blending: 2,
        transparent: true,
        depthWrite: false,
        depthTest: true,
      })

      const plane = new Mesh(geometry, material)
      plane.renderOrder = 10
      plane.position.set(x, y, z)
      plane.rotation.set(rotationX, rotationY, rotationZ)
      return plane
    }

    const createNearPlane = (x: number, y: number, z: number, rotationX: number, rotationY: number, rotationZ: number) => {
      const suiji = Math.random() * (width / 1)
      const geometry = new PlaneGeometry(width / 2 + suiji, height / 2 + suiji)
      const material = new ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: RainShader.vertexShaderNear,
        fragmentShader: RainShader.fragmentShaderNear,
        side: 2,
        blending: 2,
        transparent: true,
        depthWrite: true,
        depthTest: true,
      })

      const plane = new Mesh(geometry, material)
      plane.renderOrder = 10
      plane.position.set(x, y, z)
      plane.rotation.set(rotationX, rotationY, rotationZ)
      return plane
    }

    const count = 6
    const distance = 150
    for (let j = -count; j <= count; j++) {
      const plane = createPlane(0, 0, j * distance, 0, 0, 0)
      group.add(plane)
    }
    for (let j = -count; j <= count; j++) {
      const plane = createPlane(j * distance, 0, 0, 0, Math.PI / 2, 0)
      group.add(plane)
    }

    for (let j = -15; j <= 15; j++) {
      const plane = createNearPlane(0, 0, j * j * j * 1, 0, 0, 0)
      group.add(plane)
    }
    for (let j = -15; j <= 15; j++) {
      const plane = createNearPlane(j * j * j * 1, 0, 0, 0, Math.PI / 2, 0)
      group.add(plane)
    }

    const material2d = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: RainShader.vertexShader2d,
      fragmentShader: RainShader.fragmentShader2d,
      side: 2,
      transparent: true,
      depthWrite: false,
    })

    const rain2dGeometry = new PlaneGeometry(100, 100)
    const rain2d = new Mesh(rain2dGeometry, material2d)
    rain2d.renderOrder = 10
    group.add(rain2d)

    this.add(group)
  }

  private setUniforms(option: IRainOption) {
    Object.assign(this.option, option)

    Object.keys(option).forEach((key: string) => {
      this.uniforms[key] = { value: option[key] }
    })
  }

  public setSize(size: number) {
    this.setUniforms({ size })
  }

  public setSpeed(speed: number) {
    this.setUniforms({ snowSpeed: speed })
  }

  public setRain(scale: number) {
    this.setUniforms({ rainT1density: scale })
  }

  public setSnow(scale: number) {
    this.setUniforms({ snowT1density: scale })
  }

  public setEffect(ops: IRainOption) {
    const { size, snowT1density, rainT1density, snowSpeed } = ops
    Object.keys(ops).forEach((key: string) => {
      switch (key) {
        case 'size':
          this.setSize(size!)
          break
        case 'snowT1density':
          this.setSpeed(snowSpeed!)
          break
        case 'rainT1density':
          this.setRain(rainT1density!)
          break
        case 'snowSpeed':
          this.setSnow(snowT1density!)
          break
      }
    })
  }

  public update() {
    const { camera } = use.useScene(this.sceneControl)
    const { iTime, cameraPos } = this.uniforms
    if (iTime)
      iTime.value += 0.1
    if (cameraPos && camera)
      cameraPos.value = [camera.position.x, camera.position.y, camera.position.z]
  }

  delete() {
    this.remove()
    this.removeFromParent()
  }
}

export default RainMesh